<template>
    <c-modal :value="props.value" title="Map Styles" size="xl" @closed="handleClose">
        <div class="search-filters mb-3">
            <c-input v-model="searchInput" label="Search styles" placeholder="Type to search..." />
        </div>
        <div class="presets-content">
            <div v-if="!filteredPresets?.length" class="no-results">
                <font-awesome-icon :icon="['fa-solid', 'fa-search']" class="no-results-icon" />
                <h4>No styles found</h4>
                <p>Try adjusting your search terms</p>
            </div>
            <template v-else>
                <div class="presets-grid">
                    <map-style-thumbnail
                        v-for="(preset, index) in filteredPresets"
                        :key="index"
                        :preset="preset"
                        @select="handlePresetSelect"
                    />
                </div>
            </template>
        </div>
        <template #modal-footer>
            <div class="d-flex justify-content-end">
                <c-button text="Cancel" variant="secondary" @click="handleClose" />
            </div>
        </template>
    </c-modal>
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue';
import CModal from '@/components/CModal.vue';
import CButton from '@/components/shared/CButton';
import CInput from '@/components/shared/CInput.vue';
import MapStyleThumbnail from '@/components/DesignStudio/MapStyleProperties/components/MapStyleThumbnail.vue';
import { MAP_STYLE_PRESETS } from '@/components/DesignStudio/MapStyleProperties/presets';
import type { MapStylePresetType } from '@/components/DesignStudio/MapStyleProperties/types';

const props = defineProps<{
    value: boolean;
}>();
const emit = defineEmits<{
    (event: 'input', value: boolean): void;
    (event: 'create', value: string): void;
}>();

const searchInput = ref('');

const filteredPresets = computed(() => {
    const query = searchInput.value.toLowerCase();
    return query ? MAP_STYLE_PRESETS.filter((preset) => preset.label.toLowerCase().includes(query)) : MAP_STYLE_PRESETS;
});

const handleClose = () => {
    emit('input', false);
};

const resetForm = () => {
    searchInput.value = '';
};

const handlePresetSelect = (preset: MapStylePresetType) => {
    emit('create', preset.value);
    handleClose();
};

watch(
    () => props.value,
    (isOpen) => {
        if (!isOpen) {
            resetForm();
        }
    }
);
</script>

<style scoped lang="scss">
.no-results {
    text-align: center;
    padding: 48px 24px;
    color: #6c757d;

    .no-results-icon {
        font-size: 48px;
        margin-bottom: 16px;
        color: #dee2e6;
    }

    h4 {
        margin: 0 0 8px;
        font-size: 18px;
    }

    p {
        margin: 0;
        font-size: 14px;
    }
}

.presets-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
    gap: 16px;
    max-height: 400px;
    overflow-y: auto;
}

.grouped-presets {
    overflow-y: auto;
    .presets-grid {
        max-height: none;
        overflow-y: visible;
        margin-bottom: 0;
    }
}

.presets-grid::-webkit-scrollbar {
    width: 6px;
}

.presets-grid::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
}

.presets-grid::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;

    &:hover {
        background: #a8a8a8;
    }
}

@media (max-width: 768px) {
    .presets-grid {
        grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
        gap: 8px;
    }
}
</style>
